import { useState } from 'react';

import { useMount } from '../../hooks/useMount';
import { Nullable } from '../../types/types';

import { consoleLog, consoleWarn } from '../../utils/utils';

export interface DataFetcher<TData, TArgs extends unknown[] = []> {
  data: Nullable<TData>;
  error: Nullable<Error>;
  isFetching: boolean;
  fetch: (...args: TArgs) => Promise<Nullable<TData>>;
}

export interface DataFetcherHook<TData, TArgs extends unknown[] = []> {
  useFetcher: () => DataFetcher<TData, TArgs>;
}

interface CreateFetcherArgs<TEntity, TArgs extends unknown[]> {
  name?: string;
  fetch: (...args: TArgs) => Promise<TEntity>;
}

export function createFetcher<TData, TArgs extends unknown[]>({
  name,
  fetch,
}: CreateFetcherArgs<TData, TArgs>): DataFetcherHook<TData, TArgs> {
  const internalState: {
    listeners: Set<(version: number) => void>;
    counter: number;
    fetchPromise: Nullable<Promise<Nullable<TData>>>;
  } = {
    listeners: new Set(),
    counter: 0,
    fetchPromise: null,
  };

  const log =
    name !== undefined
      ? (...args: unknown[]) => {
          consoleLog(`[FETCHER] ${name}`, ...args);
        }
      : () => null;

  const fire = () => {
    log(`fire on ${internalState.listeners.size} listeners`);
    internalState.listeners.forEach((listener) => listener(++internalState.counter));
  };
  const dataFetcher: DataFetcher<TData, TArgs> = {
    data: null,
    isFetching: false,
    error: null,
    fetch: (...args: TArgs) =>
      new Promise<Nullable<TData>>((resolve, reject) => {
        // We need setTimeout to hack React components onMount to settle
        setTimeout(async () => {
          log('start', JSON.stringify(args));
          if (dataFetcher.isFetching) {
            log('already fetching');
            try {
              const res = internalState.fetchPromise
                ? await internalState.fetchPromise
                : await fetch(...args);
              resolve(res);
            } catch (e) {
              reject(
                e instanceof Error ? e : new Error(`Something went wrong: ${JSON.stringify(e)}`),
              );
            }
            return;
          }
          // TODO to implement
          // internalState.fetchPromise = ...new Promise<Nullable<TData>>((r) => {
          log('needs fetch');
          dataFetcher.isFetching = true;
          fire();
          try {
            log('requesting...');
            const res = await fetch(...args);
            log('...got response');
            dataFetcher.data = res;
            dataFetcher.error = null;
          } catch (e) {
            if (e instanceof Error) {
              dataFetcher.error = e;
            } else {
              dataFetcher.error = new Error(`Something went wrong: ${JSON.stringify(e)}`);
            }
            dataFetcher.data = null;
          }

          dataFetcher.isFetching = false;
          fire();

          if (dataFetcher.error === null) {
            resolve(dataFetcher.data);
          } else {
            reject(dataFetcher.error);
          }
          log('end');
        });
      }),
  };

  return {
    useFetcher: (): DataFetcher<TData, TArgs> => {
      const [, setState] = useState(internalState.counter);

      useMount(() => {
        if (internalState.listeners.has(setState)) {
          consoleWarn('Should never happen');
        }
        internalState.listeners.add(setState);

        return () => {
          internalState.listeners.delete(setState);
        };
      });

      return dataFetcher;
    },
  };
}
